import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchQuestionById, updateQuestion, getGroups } from '../../api';
import toast, { Toaster } from 'react-hot-toast';
import MyQuillEditor from '../../components/editor/MyQuillEditor';
import OptionEditor from '../../components/editor/OptionEditor';
import { 
  PencilSquareIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  XMarkIcon,
  DocumentTextIcon,
  ListBulletIcon,
  TrophyIcon
} from '@heroicons/react/24/outline';

const UpdateQuestion = () => {
  const { qid } = useParams();
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [options, setOptions] = useState([]);
  const [correctOption, setCorrectOption] = useState(null);
  const [marks, setMarks] = useState("");
  const [toption, setToption] = useState("");
  const [questionGroups, setQuestionGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [loading, setLoading] = useState(true);

  const [errors, setErrors] = useState({
    title: "",
    group: "",
    marks: "",
    options: "",
    correctOption: "",
  });

  useEffect(() => {
    fetchQuestion();
    fetchQuestionGroups();
  }, [qid]);

  useEffect(() => {
    if (toption !== "" && options.length !== parseInt(toption)) {
      setOptions(Array(parseInt(toption)).fill(""));
    }
  }, [toption, options.length]);

  const fetchQuestion = async () => {
    try {
      const data = await fetchQuestionById(qid);
      console.log(data);

      setTitle(data.question);
      setOptions(data.options.map((opt) => opt.name));
      setCorrectOption(data.options.findIndex((opt) => opt.is_correct === "1"));
      setMarks(data.mark);
      setToption(data.options.length.toString());
      setSelectedGroup(data.gid);
      toast.success('Question loaded successfully');
    } catch (error) {
      toast.error('Failed to fetch question details.');
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestionGroups = async () => {
    try {
      const data = await getGroups();
      setQuestionGroups(data);
    } catch (error) {
      toast.error('Failed to fetch question groups.');
    }
  };

  const handleOptionChange = (index, value) => {
    setOptions((prevOptions) => {
      const newOptions = [...prevOptions];
      if (newOptions[index] !== value) {
        newOptions[index] = value;
      }
      return newOptions;
    });
  };

  const handleTitleChange = (newTitle) => {
    if (newTitle !== title) {
      setTitle(newTitle);
    }
  };

  const handleOptionEditorChange = (index, value) => {
    handleOptionChange(index, value);
  };

  const validateForm = () => {
    let valid = true;
    const newErrors = {
      title: "",
      group: "",
      marks: "",
      options: "",
      correctOption: "",
    };

    if (!title.trim()) {
      newErrors.title = "Question title is required.";
      valid = false;
    }

    if (!selectedGroup) {
      newErrors.group = "Please select a question group.";
      valid = false;
    }

    if (!marks || isNaN(marks) || marks <= 0) {
      newErrors.marks = "Marks must be a positive number.";
      valid = false;
    }

    if (options.some((option) => option.trim() === "")) {
      newErrors.options = "All options must be filled in.";
      valid = false;
    }

    if (correctOption === null) {
      newErrors.correctOption = "Correct option must be selected.";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleUpdate = async () => {
    if (!validateForm()) {
      toast.error('Please fix all errors before updating');
      return;
    }

    const payload = {
      question: title,
      gid: selectedGroup,
      mark: marks,
      toption: toption,
      options: options.map((option, index) => ({
        name: option,
        isCorrect: correctOption === index,
      })),
    };

    try {
      await updateQuestion(qid, payload);
      toast.success('Question updated successfully!');
      setTimeout(() => {
        navigate('/admin/que');
      }, 1000);
    } catch (error) {
      toast.error('Failed to update question.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex justify-center items-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading question...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6">
      <Toaster position="top-right" />

      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center">
                <PencilSquareIcon className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Update Question</h1>
                <p className="text-sm text-gray-500">Question ID: {qid}</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/admin/que')}
              className="flex items-center space-x-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all border border-gray-300"
            >
              <ArrowLeftIcon className="h-5 w-5" />
              <span>Back to Questions</span>
            </button>
          </div>
        </div>

        {/* Main Form */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="space-y-6">
            {/* Question Title */}
            <div>
              <label className="flex items-center space-x-2 text-sm font-semibold text-gray-700 mb-3">
                <DocumentTextIcon className="h-5 w-5 text-blue-600" />
                <span>Question Title</span>
                <span className="text-red-500">*</span>
              </label>
              <div className="border-2 border-gray-200 rounded-xl overflow-hidden hover:border-blue-300 transition-colors">
                <MyQuillEditor
                  theme="snow"
                  value={title}
                  onChange={setTitle}
                  placeholder="Enter Question Title"
                />
              </div>
              {errors.title && (
                <p className="mt-2 text-sm text-red-600 flex items-center space-x-1">
                  <XMarkIcon className="h-4 w-4" />
                  <span>{errors.title}</span>
                </p>
              )}
            </div>

            {/* Form Controls Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Question Group */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Question Group <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedGroup}
                  onChange={(e) => setSelectedGroup(e.target.value)}
                  className={`w-full px-4 py-3 bg-white border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                    errors.group ? 'border-red-300' : 'border-gray-200'
                  }`}
                >
                  <option value="">Select Question Group</option>
                  {questionGroups.map((group) => (
                    <option key={group.gid} value={group.gid}>
                      {group.Title}
                    </option>
                  ))}
                </select>
                {errors.group && (
                  <p className="mt-1 text-sm text-red-600 flex items-center space-x-1">
                    <XMarkIcon className="h-4 w-4" />
                    <span>{errors.group}</span>
                  </p>
                )}
              </div>

              {/* Marks */}
              <div>
                <label className="flex items-center space-x-2 text-sm font-semibold text-gray-700 mb-2">
                  <TrophyIcon className="h-4 w-4 text-yellow-600" />
                  <span>Marks <span className="text-red-500">*</span></span>
                </label>
                <input
                  type="number"
                  value={marks}
                  onChange={(e) => setMarks(e.target.value)}
                  className={`w-full px-4 py-3 bg-white border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                    errors.marks ? 'border-red-300' : 'border-gray-200'
                  }`}
                  placeholder="Enter Marks"
                  min="1"
                />
                {errors.marks && (
                  <p className="mt-1 text-sm text-red-600 flex items-center space-x-1">
                    <XMarkIcon className="h-4 w-4" />
                    <span>{errors.marks}</span>
                  </p>
                )}
              </div>

              {/* Number of Options */}
              <div>
                <label className="flex items-center space-x-2 text-sm font-semibold text-gray-700 mb-2">
                  <ListBulletIcon className="h-4 w-4 text-purple-600" />
                  <span>Number of Options</span>
                </label>
                <select
                  value={toption}
                  onChange={(e) => setToption(e.target.value)}
                  className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                >
                  <option value="">Select options count</option>
                  {[3, 4, 5, 6, 7].map((count) => (
                    <option key={count} value={count}>
                      {count} Options
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Options Section */}
            {options.length > 0 && (
              <div>
                <label className="flex items-center space-x-2 text-sm font-semibold text-gray-700 mb-4">
                  <ListBulletIcon className="h-5 w-5 text-green-600" />
                  <span>Answer Options</span>
                  <span className="text-red-500">*</span>
                </label>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {options.map((option, index) => (
                    <div
                      key={index}
                      className={`relative bg-gradient-to-br ${
                        correctOption === index 
                          ? 'from-green-50 to-emerald-50 border-2 border-green-300' 
                          : 'from-gray-50 to-white border-2 border-gray-200'
                      } rounded-xl p-4 transition-all hover:shadow-md`}
                    >
                      {/* Option Label */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm ${
                            correctOption === index 
                              ? 'bg-green-500 text-white' 
                              : 'bg-gray-200 text-gray-700'
                          }`}>
                            {String.fromCharCode(65 + index)}
                          </div>
                          <span className="text-sm font-semibold text-gray-700">
                            Option {String.fromCharCode(65 + index)}
                          </span>
                        </div>
                        {correctOption === index && (
                          <CheckCircleIcon className="h-6 w-6 text-green-600" />
                        )}
                      </div>

                      {/* Option Editor */}
                      <div className="border-2 border-gray-200 rounded-lg overflow-hidden mb-3">
                        <OptionEditor
                          value={option}
                          onChange={(value) => handleOptionEditorChange(index, value)}
                          style={{ height: '100px', width: '100%' }}
                        />
                      </div>

                      {/* Correct Option Radio */}
                      <label className="flex items-center space-x-2 cursor-pointer group">
                        <input
                          type="radio"
                          name="correctOption"
                          checked={correctOption === index}
                          onChange={() => setCorrectOption(index)}
                          className="w-5 h-5 text-green-600 focus:ring-2 focus:ring-green-500"
                        />
                        <span className={`text-sm font-medium ${
                          correctOption === index ? 'text-green-700' : 'text-gray-600'
                        } group-hover:text-green-600 transition-colors`}>
                          Mark as Correct Answer
                        </span>
                      </label>
                    </div>
                  ))}
                </div>

                {errors.options && (
                  <p className="mt-3 text-sm text-red-600 flex items-center space-x-1">
                    <XMarkIcon className="h-4 w-4" />
                    <span>{errors.options}</span>
                  </p>
                )}

                {errors.correctOption && (
                  <p className="mt-3 text-sm text-red-600 flex items-center space-x-1">
                    <XMarkIcon className="h-4 w-4" />
                    <span>{errors.correctOption}</span>
                  </p>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center justify-center space-x-4 pt-6 border-t border-gray-200">
              <button
                onClick={handleUpdate}
                className="flex items-center space-x-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white px-8 py-3 rounded-xl font-semibold hover:from-green-700 hover:to-emerald-700 transform hover:scale-105 transition-all shadow-md"
              >
                <CheckCircleIcon className="h-5 w-5" />
                <span>Update Question</span>
              </button>
              <button
                onClick={() => navigate('/admin/que')}
                className="flex items-center space-x-2 bg-gray-100 text-gray-700 px-8 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-all border border-gray-300"
              >
                <XMarkIcon className="h-5 w-5" />
                <span>Cancel</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateQuestion;
